/**
 * SensorMeasurementPopup.jsx - Composant pour afficher les mesures d'un capteur
 *
 * Affiche toutes les dernières mesures (température, humidité, luminosité, pression)
 * avec gestion intelligente des formats de données multiples
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Composant d'affichage des mesures d'un capteur
 * @param {Object} props
 * @param {Object|String} props.latestMeasurement - Dernières mesures du capteur
 */
const SensorMeasurementPopup = ({ latestMeasurement }) => {
  // Debug: afficher les données reçues
  console.log('🔍 SensorMeasurementPopup - Données reçues:', latestMeasurement);
  console.log('🔍 Type:', typeof latestMeasurement);

  // Parser les mesures depuis différents formats possibles
  const measurements = (() => {
    if (!latestMeasurement) {
      console.log('❌ Pas de latestMeasurement');
      return {};
    }

    let data = latestMeasurement;

    // Si c'est une chaîne JSON, la parser
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
        console.log('🔄 JSON parsé:', data);
      } catch {
        console.log('⚠️ Échec du parsing JSON');
        return {};
      }
    }

    // Format 1: Mesure unique (legacy) - { value: 1013.25, unit: "hPa", timestamp: "..." }
    if (data.value !== undefined && data.unit !== undefined) {
      console.log('📌 Format legacy détecté (mesure unique)');

      // Déterminer le type de mesure basé sur l'unité
      let measurementType = 'Valeur';
      if (data.unit === 'hPa' || data.unit === 'Pa') {
        measurementType = 'pressure';
      } else if (data.unit === '°C' || data.unit === 'C' || data.unit === '°F') {
        measurementType = 'temperature';
      } else if (data.unit === '%' || data.unit === 'RH') {
        measurementType = 'humidity';
      } else if (data.unit === 'lux') {
        measurementType = 'luminosity';
      }

      // Convertir en objet nommé pour uniformité
      return { [measurementType]: data };
    }

    // Format 2: Tableau de mesures - [{ type: "temperature", value: 25, unit: "°C" }, ...]
    if (Array.isArray(data)) {
      console.log('📌 Format tableau détecté');
      // Convertir tableau en objet pour traitement uniforme
      const obj = {};
      data.forEach(m => {
        if (m.type && m.value !== undefined) {
          obj[m.type] = m;
        }
      });
      console.log('✅ Tableau converti en objet:', obj);
      return obj;
    }

    // Format 3: Objet multi-mesures - { temperature: {...}, humidity: {...}, ... }
    if (typeof data === 'object' && data !== null) {
      console.log('✅ Format objet multi-mesures détecté');
      return data;
    }

    console.log('⚠️ Format non reconnu, retour objet vide');
    return {};
  })();

  console.log('📊 Measurements parsés:', measurements);

  // Ordre d'affichage préféré des mesures
  const measurementOrder = ['temperature', 'humidity', 'luminosity', 'pressure'];

  // Labels français pour les types de mesures
  const labels = {
    temperature: 'Température',
    humidity: 'Humidité',
    luminosity: 'Luminosité',
    pressure: 'Pression',
    light: 'Lumière',
    soil_moisture: 'Humidité du sol',
    ph: 'pH',
    ec: 'Conductivité',
    co2: 'CO₂'
  };

  // Filtrer et trier les mesures
  const sortedMeasurements = Object.entries(measurements)
    .filter(([key, data]) => {
      // Garder seulement les objets avec value et unit
      const isValid = data && typeof data === 'object' && data.value !== undefined;
      console.log(`🔎 Filtrage "${key}":`, { data, isValid });
      return isValid;
    })
    .sort((a, b) => {
      const aIdx = measurementOrder.indexOf(a[0].toLowerCase());
      const bIdx = measurementOrder.indexOf(b[0].toLowerCase());
      return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
    });

  console.log('📋 Mesures triées:', sortedMeasurements);

  // Si aucune mesure valide, ne rien afficher
  if (sortedMeasurements.length === 0) {
    console.log('⚠️ Aucune mesure valide trouvée, retour null');
    return null;
  }

  return (
    <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
      <Typography variant="caption" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
        {sortedMeasurements.length === 1 ? 'Dernière mesure:' : 'Dernières mesures:'}
      </Typography>
      {sortedMeasurements.map(([type, data]) => {
        const label = labels[type.toLowerCase()] || type;

        return (
          <Box key={type} sx={{ mb: 0.5, '&:last-child': { mb: 0 } }}>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              <strong>{label}:</strong> {data.value} {data.unit}
            </Typography>
            {data.timestamp && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ fontSize: '0.7rem', ml: 0.5 }}
              >
                {new Date(data.timestamp).toLocaleString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default SensorMeasurementPopup;
